import { RESEARCH } from "@/lib/research";

/**
 * Scroll-animated "studied effects" numbers shown on each product page.
 *
 * Every figure below is derived from the project's verified research library
 * (src/lib/research.ts) or from the locked label doses — no invented
 * statistics, and no finished-product outcome claims. Each card links to a
 * real peer-reviewed citation and opens the research drawer for that active.
 */

export type EffectStat = {
  /** Links the card to RESEARCH[] so "Read more" opens that ingredient. */
  slug?: string;
  value: number;
  prefix?: string;
  suffix?: string;
  /** Card heading, e.g. "Published studies". */
  label: string;
  /** Line under the number, e.g. "peer-reviewed papers". */
  qualifier: string;
  /** Which active or formulation element the number belongs to. */
  active: string;
  /** Provenance line, e.g. "PubMed-indexed literature". */
  design: string;
  citationUrl?: string;
  citationLabel?: string;
};

type SkuKey = "mag" | "liver" | "ova";

/** Parses "1,200+ studies" -> 1200. */
function studyCountValue(slug: string): number {
  const raw = RESEARCH.find((r) => r.slug === slug)?.studyCount ?? "";
  const n = Number(raw.replace(/[^0-9]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function firstCitation(slug: string) {
  const r = RESEARCH.find((x) => x.slug === slug);
  const c = r?.citations[0];
  return c ? { url: c.url, label: `${c.journal} (${c.year})` } : {};
}

function studyStat(slug: string, qualifier: string, design: string): EffectStat {
  const r = RESEARCH.find((x) => x.slug === slug);
  const c = firstCitation(slug);
  return {
    slug,
    value: studyCountValue(slug),
    suffix: "+",
    label: r?.name ?? slug,
    qualifier: `published studies ${qualifier}`,
    active: "PubMed-indexed literature",
    design,
    citationUrl: c.url,
    citationLabel: c.label,
  };
}

/** Total peer-reviewed papers cited across every active in a SKU. */
function citationsForSku(sku: SkuKey): number {
  return RESEARCH.filter((r) => r.sku === sku).reduce(
    (sum, r) => sum + r.citations.length,
    0,
  );
}

/** Earliest cited publication year across a SKU's actives. */
function researchSpanYears(sku: SkuKey): number {
  const years = RESEARCH.filter((r) => r.sku === sku).flatMap((r) =>
    r.citations.map((c) => c.year),
  );
  if (years.length === 0) return 0;
  return new Date().getFullYear() - Math.min(...years);
}

export type EffectBlock = {
  eyebrow: string;
  title: string;
  intro: string;
  stats: EffectStat[];
};

export function getEffectBlock(sku: SkuKey): EffectBlock {
  const shared = (): EffectStat[] => [
    {
      value: citationsForSku(sku),
      label: "Papers cited",
      qualifier: "peer-reviewed references",
      active: "This formulation's actives",
      design: "Each citation opens on PubMed",
    },
    {
      value: researchSpanYears(sku),
      suffix: " yrs",
      label: "Research span",
      qualifier: "earliest cited study to today",
      active: "Across all actives",
      design: "Verified publication years",
    },
  ];

  if (sku === "mag") {
    return {
      eyebrow: "The evidence behind Mag Absorb Pro",
      title: "What the research on these actives reports",
      intro:
        "Five magnesium forms, each chosen from published literature. Tap any card to read the studies behind it.",
      stats: [
        studyStat(
          "magnesium-bisglycinate",
          "on the bisglycinate chelate",
          "Includes a double-blind placebo-controlled insomnia RCT",
        ),
        studyStat(
          "aquamin-marine-magnesium",
          "on Aquamin® marine multi-mineral",
          "Includes randomised placebo-controlled trials",
        ),
        {
          value: 440,
          suffix: " mg",
          label: "Elemental magnesium",
          qualifier: "per daily serving, across 5 forms",
          active: "Liposomal Bisglycinate · Aquamin® · Chloride · Aspartate · Gluconate",
          design: "Locked label dose",
        },
        ...shared(),
      ],
    };
  }

  if (sku === "liver") {
    return {
      eyebrow: "The evidence behind Liver Biome Plus",
      title: "What the research on these actives reports",
      intro:
        "Liposomal hepatoprotective actives plus a probiotic consortium, each selected from published literature.",
      stats: [
        studyStat("nac", "on N-Acetyl Cysteine", "Includes clinical hepatology trials"),
        studyStat(
          "silymarin",
          "on milk thistle silymarin",
          "Includes pharmacokinetic and clinical studies",
        ),
        {
          value: 600,
          suffix: " mg",
          label: "NAC per serving",
          qualifier: "glutathione precursor dose",
          active: "N-Acetyl Cysteine",
          design: "Locked label dose",
        },
        ...shared(),
      ],
    };
  }

  return {
    eyebrow: "The evidence behind Ova Balance",
    title: "What the research on these actives reports",
    intro:
      "A 40:1 inositol core with Berberine HCl and bioactive folate, each selected from published literature.",
    stats: [
      studyStat(
        "myo-inositol",
        "on myo-inositol in PCOS",
        "Includes randomised trials and meta-analyses",
      ),
      studyStat(
        "berberine",
        "on berberine",
        "Includes metabolic RCTs and PCOS meta-analyses",
      ),
      {
        value: 2050,
        suffix: " mg",
        label: "Inositol complex",
        qualifier: "Myo : D-Chiro in the 40:1 ratio",
        active: "Myo-Inositol + D-Chiro Inositol",
        design: "Locked label dose",
      },
      ...shared(),
    ],
  };
}
